import type { BotMenu } from "@velizhanin-dev/bot-scenario"
import type {
	DraftEdge,
	DraftEntryLink,
	DraftFlow,
	DraftNode,
	DraftTag,
	DraftVariable,
	NodePosition,
	ScenarioDraft,
} from "../api/types"

/**
 * Применение ответов api к черновику в кэше React Query. Чистые функции:
 * черновик не перезагружается после каждой правки.
 */

/** keepPosition: ответ пришёл, пока на сервер ещё уходила новая раскладка: координаты локальные свежее. */
export function upsertNode(
	draft: ScenarioDraft,
	node: DraftNode,
	options: { keepPosition?: boolean } = {},
): ScenarioDraft {
	const existing = draft.nodes.find((item) => item.id === node.id)
	if (!existing) return { ...draft, nodes: [...draft.nodes, node] }
	const next = options.keepPosition
		? { ...node, positionX: existing.positionX, positionY: existing.positionY }
		: node
	return { ...draft, nodes: draft.nodes.map((item) => (item.id === node.id ? next : item)) }
}

export function removeNode(draft: ScenarioDraft, nodeId: string): ScenarioDraft {
	return {
		...draft,
		nodes: draft.nodes.filter((node) => node.id !== nodeId),
		edges: draft.edges.filter(
			(edge) => edge.sourceNodeId !== nodeId && edge.targetNodeId !== nodeId,
		),
	}
}

/** С одного выхода: одна связь: новая вытесняет прежнюю с того же порта. */
export function upsertEdge(draft: ScenarioDraft, edge: DraftEdge): ScenarioDraft {
	const others = draft.edges.filter(
		(item) =>
			item.id !== edge.id &&
			!(item.sourceNodeId === edge.sourceNodeId && item.sourcePort === edge.sourcePort),
	)
	return { ...draft, edges: [...others, edge] }
}

export function removeEdges(draft: ScenarioDraft, edgeIds: string[]): ScenarioDraft {
	if (edgeIds.length === 0) return draft
	const removed = new Set(edgeIds)
	return { ...draft, edges: draft.edges.filter((edge) => !removed.has(edge.id)) }
}

export function upsertFlow(draft: ScenarioDraft, flow: DraftFlow): ScenarioDraft {
	const flows = draft.flows.some((item) => item.id === flow.id)
		? draft.flows.map((item) => (item.id === flow.id ? flow : item))
		: [...draft.flows, flow]
	return { ...draft, flows: [...flows].sort((a, b) => a.sortOrder - b.sortOrder) }
}

export function removeFlow(draft: ScenarioDraft, flowId: string): ScenarioDraft {
	const nodeIds = new Set(
		draft.nodes.filter((node) => node.flowId === flowId).map((node) => node.id),
	)
	return {
		...draft,
		flows: draft.flows.filter((flow) => flow.id !== flowId),
		nodes: draft.nodes.filter((node) => !nodeIds.has(node.id)),
		edges: draft.edges.filter(
			(edge) => !nodeIds.has(edge.sourceNodeId) && !nodeIds.has(edge.targetNodeId),
		),
	}
}

export function upsertVariable(draft: ScenarioDraft, variable: DraftVariable): ScenarioDraft {
	const variables = draft.variables.some((item) => item.id === variable.id)
		? draft.variables.map((item) => (item.id === variable.id ? variable : item))
		: [...draft.variables, variable]
	return { ...draft, variables: [...variables].sort((a, b) => a.name.localeCompare(b.name)) }
}

export function removeVariable(draft: ScenarioDraft, variableId: string): ScenarioDraft {
	return { ...draft, variables: draft.variables.filter((variable) => variable.id !== variableId) }
}

export function upsertTag(draft: ScenarioDraft, tag: DraftTag): ScenarioDraft {
	const tags = draft.tags ?? []
	const next = tags.some((item) => item.id === tag.id)
		? tags.map((item) => (item.id === tag.id ? tag : item))
		: [...tags, tag]
	return { ...draft, tags: [...next].sort((a, b) => a.name.localeCompare(b.name, "ru")) }
}

export function setTagsArchived(
	draft: ScenarioDraft,
	tagIds: string[],
	archived: boolean,
): ScenarioDraft {
	const ids = new Set(tagIds)
	const archivedAt = archived ? new Date().toISOString() : null
	return {
		...draft,
		tags: (draft.tags ?? []).map((tag) => (ids.has(tag.id) ? { ...tag, archivedAt } : tag)),
	}
}

/** Группы, которые предлагаются в выборе: без архива, но уже выбранные остаются на виду. */
export function pickableTags<T extends { id: string; archivedAt?: string | null }>(tags: T[], selectedIds: Iterable<string> = []): T[] {
	const keep = new Set(selectedIds)
	return tags.filter((tag) => !tag.archivedAt || keep.has(tag.id))
}

/** Из ссылок входа удалённая метка тоже уходит, так делает api. */
export function removeTag(draft: ScenarioDraft, tagId: string): ScenarioDraft {
	return {
		...draft,
		tags: (draft.tags ?? []).filter((tag) => tag.id !== tagId),
		entryLinks: (draft.entryLinks ?? []).map((link) =>
			link.addTagIds.includes(tagId)
				? { ...link, addTagIds: link.addTagIds.filter((id) => id !== tagId) }
				: link,
		),
	}
}

export function upsertEntryLink(draft: ScenarioDraft, link: DraftEntryLink): ScenarioDraft {
	const links = draft.entryLinks ?? []
	const next = links.some((item) => item.id === link.id)
		? links.map((item) => (item.id === link.id ? link : item))
		: [...links, link]
	return { ...draft, entryLinks: [...next].sort((a, b) => a.code.localeCompare(b.code)) }
}

export function removeEntryLink(draft: ScenarioDraft, linkId: string): ScenarioDraft {
	return { ...draft, entryLinks: (draft.entryLinks ?? []).filter((link) => link.id !== linkId) }
}

export function setMenu(draft: ScenarioDraft, menu: BotMenu | null): ScenarioDraft {
	return { ...draft, menu }
}

export function applyPositions(draft: ScenarioDraft, positions: NodePosition[]): ScenarioDraft {
	const byId = new Map(positions.map((position) => [position.nodeId, position]))
	return {
		...draft,
		nodes: draft.nodes.map((node) => {
			const position = byId.get(node.id)
			return position ? { ...node, positionX: position.x, positionY: position.y } : node
		}),
	}
}
